import { _GettersTree, defineStore } from "pinia";
import { LessonPlanCollection } from "@/models/LessonPlanCollection.model";
import { useUsersStore } from "@/stores/Users.store";
import { useDefaultOrderStore } from "@/stores/DefaultOrder.store";
import { AbstractBaseStore, AbstractStoreState } from "@/stores/AbstractBase.store";
import { api } from "@/utils/Api.util";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag";
import { LessonPlan } from "@/models/LessonPlan.model";
import { i18n } from '@/i18n'

interface lessonPlanCollectionsStoreState extends AbstractStoreState<LessonPlanCollection, any> {
}

const BaseStore = AbstractBaseStore<LessonPlanCollection, lessonPlanCollectionsStoreState, any>();


export const useLessonPlanCollectionsStore = defineStore("lessonPlanCollections", {

	state: (): lessonPlanCollectionsStoreState => {
		return BaseStore.state({
			order: useDefaultOrderStore().lessonPlanCollectionsOrder,
		});
	},

	getters: {
		...BaseStore.getters,

		apiClient() {
			return api;
		},

		apiPath() {
			return 'users/' + useUsersStore().getMe().getId() + '/lesson-plan-collections';
		},

		/**
		 * Get the default mask that should be included in every request.
		 * @param state
		 * @returns
		 */
		defaultMask(state): string[] {
			return [
				"id",
				"name",
				"color",
				"planCount",
				"sharing",
				"sharingWithSubjectId",
			];
		},

		/**
		 * @returns number
		 */
		defaultRecordsPerPage(): number {
			return 1000;
		},


	} as _GettersTree<lessonPlanCollectionsStoreState>,

	actions: {

		...BaseStore.actions,

		/**
		 * Add a lesson plan to a collection.
		 * @param collection The collection to add to
		 * @param lessonPlan The lesson plan to add
		 */
		async addLessonPlanToCollection(collection: LessonPlanCollection, lessonPlan: LessonPlan): Promise<void> {
			if (!collection || !lessonPlan?.id) {
				throw new Error('Collection and lesson plan are required');
			}

			// Check if lesson plan is already in collection
			if (!collection.id || lessonPlan.collectionIds?.includes(collection.id)) {
				return;
			}

			try {
				const userId = useUsersStore().getMe().getId();

				const response = await this.apiClient.post(
					`users/${userId}/lesson-plan-collections/${collection.id}/lesson-plans`,
					{ items: [{ id: lessonPlan.id }] }
				);

				if (!lessonPlan.collectionIds) {
					lessonPlan.collectionIds = [];
				}

				if (!lessonPlan.collectionIds.includes(collection.id)) {
					lessonPlan.collectionIds.push(collection.id);
				}

				// Update collection from API response
				if (response.data?.data) {
					const updatedCollection = this.mapToItem(response.data.data);
					this.updateStoreItem(updatedCollection);
				}

			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		},

		/**
		 * Remove a lesson plan from a collection.
		 * @param collection The collection to remove from
		 * @param lessonPlan The lesson plan to remove
		 */
		async removeLessonPlanFromCollection(collection: LessonPlanCollection, lessonPlan: LessonPlan): Promise<void> {

			// Check if lesson plan is already in collection
			if (!collection.id || !lessonPlan.collectionIds?.includes(collection.id)) {
				return;
			}

			try {
				const userId = useUsersStore().getMe().getId();

				const response = await this.apiClient.delete(
					`users/${userId}/lesson-plan-collections/${collection.id}/lesson-plans`,
					{ data: { items: [{ id: lessonPlan.id }] } }
				);

				// Update the lesson plan's collectionIds if a LessonPlan object was provided
				lessonPlan.collectionIds = lessonPlan.collectionIds.filter(id => id !== collection.id);

				// Update collection from API response
				if (response.data?.data) {
					const updatedCollection = this.mapToItem(response.data.data);
					this.updateStoreItem(updatedCollection);
				}

			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		},

		/**
		 * Create and save a new lesson plan collection
		 * @param name The name of the collection
		 * @returns The saved collection
		 */
		async createCollection(name: string, color: string): Promise<LessonPlanCollection> {
			if (!name?.trim()) {
				throw new Error('List name is required');
			}
			const newList = new LessonPlanCollection();
			newList.name = name.trim();
			newList.color = color;

			await this.save(newList);

			return newList;
		},

		mapToItem(data: any): LessonPlanCollection {
			const item = LessonPlanCollection.createFromApiData(data);
			if (item.name?.startsWith('$')) {
				// @ts-ignore
				item.name = i18n.global.t('#lesson plan collection: ' + item.name.substring(1));
			}
			return item;
		},

		/**
		 * Merge one or multiple collections into another by moving all lesson plans from the source collection to the target collection, then optionally deleting the source collection.
		 * @param targetCollection
		 * @param sourceCollections
		 * @param removeSource
		 */
		async mergeCollections(
			targetCollection: LessonPlanCollection,
			sourceCollections: LessonPlanCollection[],
			removeSource: boolean = false
		): Promise<{ sourceCollections: LessonPlanCollection[]; targetCollection: LessonPlanCollection }> {
			if (!targetCollection.id || !sourceCollections || sourceCollections.length === 0 || sourceCollections.some(l => !l.id)) {
				throw new Error('Source and target collection IDs are required');
			}

			try {

				// Execute api requests
				const userId = useUsersStore().getMe().getId();

				const targetCollectionData = await this.apiClient.post(
					`users/${userId}/lesson-plan-collections/${targetCollection.id}/transfer`,
					{
						items: sourceCollections.map(l => ({ id: l.id })),
						targetCollectionId: targetCollection.id,
						removeSource
					}
				);

				// All source collections will be altered (have lesson plans removed)
				sourceCollections.forEach((sourceList: LessonPlanCollection) => {
					sourceList.planCount = 0;
					this.updateStoreItem(sourceList);
				});

				const updatedTargetList = this.mapToItem(targetCollectionData.data.data);
				this.updateOrAddStoreItem(updatedTargetList);

				// Also process the 'altered' property that is sent as meta-data.
				// (this is what removes the source collection from the store, if applicable)
				this.afterResponseWithAltered(targetCollectionData);

				return {
					sourceCollections,
					targetCollection: updatedTargetList
				};

			} catch (e: any) {
				this.errors = ApiErrorMessageBag.fromAxiosException(e);
				throw this.errors;
			}
		},

		shouldSortLocally() {
			return true;
		},
	},
});
